import React, { useEffect, useState } from 'react';
import { AlertTriangle, Archive, Download, FileJson, RefreshCw, RotateCcw, ShieldCheck, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiErrorMessage, backupAPI } from '../services/api';

interface ServerBackup {
  name: string;
  path: string;
  host_path_hint?: string;
  size_bytes: number;
  created_at: string;
}

function formatBytes(bytes: number) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const idx = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, idx)).toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
}

export default function BackupRestore() {
  const [backups, setBackups] = useState<ServerBackup[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [savingServer, setSavingServer] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoringServerName, setRestoringServerName] = useState('');
  const [selectedBackup, setSelectedBackup] = useState<any | null>(null);
  const [selectedName, setSelectedName] = useState('');
  const [confirmRestore, setConfirmRestore] = useState('');

  const loadBackups = async () => {
    setLoading(true);
    try {
      const response = await backupAPI.listBackups();
      setBackups(response.data.backups || []);
    } catch (error: any) {
      toast.error('Failed to load backup list: ' + apiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBackups();
  }, []);

  const downloadBackup = async () => {
    setExporting(true);
    try {
      const response = await backupAPI.exportBackup();
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename=([^;]+)/);
      const filename = match?.[1]?.replaceAll('"', '') || `strongswan-manager-backup-${new Date().toISOString()}.json`;
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
      await loadBackups();
    } catch (error: any) {
      toast.error('Backup export failed: ' + apiErrorMessage(error));
    } finally {
      setExporting(false);
    }
  };

  const saveFilesystemBackup = async () => {
    setSavingServer(true);
    try {
      const response = await backupAPI.createServerBackup();
      toast.success(`Saved backup to ${response.data.host_path_hint || response.data.path}`);
      await loadBackups();
    } catch (error: any) {
      toast.error('Filesystem backup failed: ' + apiErrorMessage(error));
    } finally {
      setSavingServer(false);
    }
  };

  const downloadServerBackup = async (backup: ServerBackup) => {
    try {
      const response = await backupAPI.downloadServerBackup(backup.name);
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = backup.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Server backup downloaded');
    } catch (error: any) {
      toast.error('Download failed: ' + apiErrorMessage(error));
    }
  };

  const restoreServerBackup = async (backup: ServerBackup) => {
    const confirmed = window.confirm(`Restore filesystem backup ${backup.name}? A pre-restore safety snapshot will be created first.`);
    if (!confirmed) return;
    setRestoringServerName(backup.name);
    try {
      const response = await backupAPI.restoreServerBackup(backup.name);
      toast.success(response.data.message || 'Server backup restored');
      await loadBackups();
    } catch (error: any) {
      toast.error('Restore failed: ' + apiErrorMessage(error));
    } finally {
      setRestoringServerName('');
    }
  };

  const onFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      setSelectedBackup(parsed);
      setSelectedName(file.name);
      setConfirmRestore('');
      toast.success('Backup file loaded for review');
    } catch (error: any) {
      setSelectedBackup(null);
      setSelectedName('');
      toast.error('Could not read backup JSON: ' + (error.message || String(error)));
    }
  };

  const restoreBackup = async () => {
    if (!selectedBackup) {
      toast.error('Choose a backup JSON file first');
      return;
    }
    if (confirmRestore !== 'RESTORE') {
      toast.error('Type RESTORE to confirm');
      return;
    }
    setRestoring(true);
    try {
      const response = await backupAPI.restoreBackup(selectedBackup);
      toast.success(response.data.message || 'Backup restored');
      setSelectedBackup(null);
      setSelectedName('');
      setConfirmRestore('');
      await loadBackups();
    } catch (error: any) {
      toast.error('Restore failed: ' + apiErrorMessage(error));
    } finally {
      setRestoring(false);
    }
  };

  const backupConnectionCount = selectedBackup?.connections?.length ?? 0;
  const hasSecrets = Boolean(selectedBackup?.ipsec?.ipsec_secrets);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
            <Archive className="h-4 w-4" /> Config safety net
          </div>
          <h1 className="mt-3 text-3xl font-bold text-gray-900">Backup & restore VPN manager</h1>
          <p className="mt-2 max-w-3xl text-gray-600">
            Export customer VPN records, StrongSwan config files, and managed PSKs before changes. Restore creates a server-side safety snapshot first, then reloads StrongSwan.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            onClick={saveFilesystemBackup}
            disabled={savingServer}
            className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <Archive className={`mr-2 h-4 w-4 ${savingServer ? 'animate-pulse' : ''}`} />
            {savingServer ? 'Saving...' : 'Save to server filesystem'}
          </button>
          <button
            onClick={downloadBackup}
            disabled={exporting}
            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Download className={`mr-2 h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Creating backup...' : 'Download backup now'}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-900">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-none" />
          <div>
            <h2 className="font-semibold">Treat backup files like secrets</h2>
            <p className="mt-1 text-sm">
              Backups include <code className="rounded bg-amber-100 px-1">/etc/ipsec.secrets</code>, which contains PSKs needed to bring tunnels back up. Store downloaded files somewhere private.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="flex items-center text-xl font-semibold text-gray-900"><Upload className="mr-2 h-5 w-5 text-blue-600" /> Restore from backup</h2>
              <p className="mt-1 text-sm text-gray-600">Upload a JSON backup. A pre-restore snapshot is saved automatically on the server.</p>
            </div>
          </div>

          <label className="mt-5 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center hover:border-blue-400 hover:bg-blue-50">
            <FileJson className="h-10 w-10 text-gray-400" />
            <span className="mt-3 font-medium text-gray-900">Choose backup JSON</span>
            <span className="mt-1 text-sm text-gray-500">No restore happens until you review and confirm.</span>
            <input type="file" accept="application/json,.json" className="hidden" onChange={onFileSelected} />
          </label>

          {selectedBackup && (
            <div className="mt-5 space-y-4 rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-2 font-semibold text-gray-900"><ShieldCheck className="h-5 w-5 text-green-600" /> Backup loaded: {selectedName}</div>
              <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                <div className="rounded-xl bg-white p-3"><div className="text-gray-500">Format</div><div className="font-mono text-xs text-gray-900">{selectedBackup.format || 'unknown'}</div></div>
                <div className="rounded-xl bg-white p-3"><div className="text-gray-500">Created</div><div className="font-mono text-xs text-gray-900">{selectedBackup.created_at || 'unknown'}</div></div>
                <div className="rounded-xl bg-white p-3"><div className="text-gray-500">Connections</div><div className="font-semibold text-gray-900">{backupConnectionCount}</div></div>
                <div className="rounded-xl bg-white p-3"><div className="text-gray-500">Includes PSKs</div><div className={`font-semibold ${hasSecrets ? 'text-amber-700' : 'text-gray-900'}`}>{hasSecrets ? 'Yes — sensitive' : 'No'}</div></div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Type RESTORE to confirm</label>
                <input value={confirmRestore} onChange={(e) => setConfirmRestore(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="RESTORE" />
              </div>
              <button
                onClick={restoreBackup}
                disabled={restoring || confirmRestore !== 'RESTORE'}
                className="inline-flex w-full items-center justify-center rounded-lg bg-red-600 px-4 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                <RotateCcw className="mr-2 h-4 w-4" /> {restoring ? 'Restoring...' : 'Restore backup and reload StrongSwan'}
              </button>
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Server filesystem snapshots</h2>
              <p className="mt-1 text-sm text-gray-600">Recent backups saved on the VPS host at <code className="rounded bg-gray-100 px-1">/root/strongswan-manager-app/data/backups</code>.</p>
            </div>
            <button onClick={loadBackups} disabled={loading} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <RefreshCw className={`inline h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="mt-5 space-y-3">
            {backups.length === 0 && <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-600">No server-side backup snapshots yet.</div>}
            {backups.map((backup) => (
              <div key={backup.path} className="rounded-xl border border-gray-200 p-4">
                <div className="font-medium text-gray-900">{backup.name}</div>
                <div className="mt-1 text-xs text-gray-500">{new Date(backup.created_at).toLocaleString()} · {formatBytes(backup.size_bytes)}</div>
                <div className="mt-2 truncate font-mono text-xs text-gray-400">{backup.host_path_hint || backup.path}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => downloadServerBackup(backup)} className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                    <Download className="mr-1 inline h-3.5 w-3.5" /> Download
                  </button>
                  <button
                    onClick={() => restoreServerBackup(backup)}
                    disabled={restoringServerName === backup.name}
                    className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    <RotateCcw className="mr-1 inline h-3.5 w-3.5" /> {restoringServerName === backup.name ? 'Restoring...' : 'Restore this'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
